<template>
  <div class="work-instructions-list">
    <div class="header">
      <div class="list-title">
        <editor-module-tabs />
      </div>

      <div class="right-section">
        <div class="search">
          <form @submit.prevent="fetchRows">
            <b-field>
              <b-input
                v-model="search"
                :placeholder="$t('search')"
                type="search"
                icon="magnify"
                icon-clickable
                @icon-click="fetchRows"
                class="search-input"
              />
            </b-field>
          </form>
        </div>

        <b-button class="wi-add-button" @click="onAddWorkInstruction">
          {{ $t('work_instructions.add_button') }}
        </b-button>

        <b-button
          type="is-success"
          icon-left="recycle"
          class="dij-jobs-recycle-bin"
          @click="showDeletedJobs"
        />
      </div>
    </div>

    <CreateStandardWorkInstructionModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @save="onSaveNewWorkInstruction"
    />

    <CreateStandardWorkInstructionModal
      v-if="showEditModal"
      :initial-data="editingRow"
      @close="showEditModal = false"
      @save="onSaveEdit"
    />

    <div class="items">
      <div class="items-container">
        <b-table
          :data="filteredRows"
          :hoverable="true"
          :loading="isLoading"
          paginated
          :pagination-simple="true"
          :per-page="10"
          default-sort="name"
          @click="openWorkInstruction"
        >
          <template slot-scope="props">
            <b-table-column
              field="name"
              :label="$t('work_instructions.table.name')"
              searchable
            >
              {{ props.row.name }}
            </b-table-column>
            <b-table-column
              field="description"
              :label="$t('work_instructions.table.description')"
              searchable
            >
              {{ props.row.description }}
            </b-table-column>
            <b-table-column
              field="version"
              :label="$t('work_instructions.table.version')"
              searchable
            >
              {{ props.row.version }}
            </b-table-column>
            <b-table-column
              field="owner"
              :label="$t('work_instructions.table.owner')"
              searchable
            >
              {{ props.row.owner }}
            </b-table-column>
            <b-table-column
              field="status"
              :label="$t('work_instructions.table.status')"
              searchable
            >
              {{ props.row.status }}
            </b-table-column>
            <b-table-column
              field="linkedWorkInstructions"
              :label="$t('work_instructions.table.linked_work_instructions')"
            >
              {{ props.row.linkedWorkInstructions }}
            </b-table-column>
            <b-table-column
              field="linkedTasks"
              :label="$t('work_instructions.table.linked_tasks')"
            >
              {{ props.row.linkedTasks }}
            </b-table-column>
            <b-table-column
              field="linkedTemplates"
              :label="$t('work_instructions.table.linked_templates')"
            >
              {{ props.row.linkedTemplates }}
            </b-table-column>
            <b-table-column
              field="date"
              :label="$t('work_instructions.table.date')"
              searchable
            >
              {{ props.row.date }}
            </b-table-column>
            <b-table-column
              field="favorite"
              :label="$t('work_instructions.table.favorite')"
            >
              <span class="favorite-cell">
                <b-icon icon="star" :class="{ active: props.row.isFavorite }" />
                {{ props.row.favoriteCount }}
              </span>
            </b-table-column>
            <b-table-column custom-key="actions" class="options-column">
              <div @click.stop>
                <b-dropdown
                  aria-role="list"
                  :position="props.index > 1 ? 'is-top-left' : 'is-bottom-left'"
                >
                  <button class="button options-more-btn" slot="trigger">
                    <b-icon icon="dots-horizontal"></b-icon>
                  </button>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEdit(props.row)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="pencil"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('edit') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDelete(props.row)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="delete"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('delete') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-table-column>
          </template>
          <template #empty>
            <section class="section wi-empty-state">
              <div class="content has-text-grey has-text-centered">
                <p>
                  {{
                    search
                      ? $t('work_instructions.empty_search')
                      : $t('work_instructions.empty_default')
                  }}
                </p>
              </div>
            </section>
          </template>
        </b-table>
      </div>
    </div>

    <b-modal
      :active="isDeletedJobsVisible"
      has-modal-card
      trap-focus
      class="dij-jobs-deleted-modal"
      @close="hideDeletedJobs"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <b-icon icon="recycle" size="is-large" />
          <p class="modal-card-title">{{ $t('recycle_bin') }}</p>
        </header>
        <section class="modal-card-body">
          <template v-if="deletedJobs.length > 0">
            <b-table :data="deletedJobs">
              <template slot-scope="props">
                <b-table-column field="title" :label="$t('title')">
                  {{ props.row.title }}
                </b-table-column>
                <b-table-column field="owner" :label="$t('owner')">
                  {{ props.row.owner }}
                </b-table-column>
                <b-table-column field="deletedAt" :label="$t('deleted_at')">
                  {{ formatDate(props.row.deletedAt) }}
                </b-table-column>
                <b-table-column :label="$t('actions')">
                  <b-button
                    class="dij-recycle-bin-recover"
                    type="is-primary"
                    icon-left="backup-restore"
                    @click="undelete(props.row._id)"
                  />
                </b-table-column>
              </template>
            </b-table>
          </template>
          <template v-else>
            {{ $t('recycle_bin_empty') }}
          </template>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { v4 } from 'uuid';
import client from '../../config/api';
import EditorModuleTabs from './ModuleTabs';
import CreateStandardWorkInstructionModal from '../Shared/WorkInstructions/CreateStandardWorkInstructionModal';

const uuid = v4;

export default {
  name: 'editor-work-instructions-list',
  components: {
    EditorModuleTabs,
    CreateStandardWorkInstructionModal,
  },
  data() {
    return {
      isLoading: false,
      search: '',
      rows: [],
      deletedRows: [],
      isDeletedJobsVisible: false,
      showCreateModal: false,
      showEditModal: false,
      editingRow: null,
    };
  },
  computed: {
    deletedJobs() {
      return this.deletedRows;
    },
    filteredRows() {
      const term = (this.search || '').toLowerCase().trim();
      if (!term) return this.rows;

      return this.rows.filter((row) =>
        [
          row.name,
          row.description,
          row.version,
          row.owner,
          row.status,
          row.linkedWorkInstructions,
          row.linkedTasks,
          row.linkedTemplates,
          row.date,
          row.favoriteCount,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term)
      );
    },
  },
  async created() {
    await this.fetchRows();
    await this.fetchDeletedRows();
  },
  methods: {
    isWorkInstructionType(wiPackage) {
      const type = (wiPackage?.type || '').toString().trim();
      return type === 'standard_work_instruction_package';
    },

    normalizeSelectionValue(value) {
      if (Array.isArray(value)) {
        return this.normalizeSelectionValue(value[0]);
      }

      if (value && typeof value === 'object') {
        return (
          value.name ||
          value.text ||
          value.value ||
          value.label ||
          ''
        ).toString();
      }

      return value == null ? '' : value.toString();
    },

    getFirstNormalizedValue(...values) {
      return (
        values
          .map((value) => this.normalizeSelectionValue(value).trim())
          .find((normalized) => Boolean(normalized)) || null
      );
    },

    async fetchRows() {
      this.isLoading = true;
      try {
        const response = await client.get('/work-instructions-package');
        const packages = response?.data || [];
        const workInstructionPackages = packages.filter(
          (wiPackage) =>
            wiPackage.isStandard && this.isWorkInstructionType(wiPackage)
        );
        const workInstructionPackageIds = new Set(
          workInstructionPackages
            .map((wiPackage) => wiPackage._id)
            .filter(Boolean)
        );
        const instructionPackages = packages.filter(
          (wiPackage) =>
            wiPackage.isStandard &&
            !this.isWorkInstructionType(wiPackage) &&
            !workInstructionPackageIds.has(wiPackage._id)
        );

        this.rows = workInstructionPackages.map((wiPackage) => {
          const wiInstructionIds = new Set(
            (wiPackage.workInstructions || []).map((id) => String(id))
          );
          const linkedWorkInstructionsCount = instructionPackages.filter(
            (instructionPackage) =>
              (instructionPackage.workInstructions || []).some((id) =>
                wiInstructionIds.has(String(id))
              )
          ).length;

          return {
            id: wiPackage._id,
            routeId: this.resolveRoutePackageId(wiPackage),
            name: wiPackage.title || '-',
            description: wiPackage.category || wiPackage.description || '-',
            version: wiPackage.version || wiPackage.revision || '-',
            owner:
              wiPackage.owner || this.$store.state.currentUser?.fullname || '-',
            status: wiPackage.status || 'Draft',
            linkedWorkInstructions: linkedWorkInstructionsCount,
            linkedTasks: '...',
            linkedTemplates: '...',
            date: wiPackage.createdAt
              ? this.formatWIDate(wiPackage.createdAt)
              : '-',
            isFavorite: false,
            favoriteCount: wiPackage.favoriteCount || 0,
            productLine: this.getFirstNormalizedValue(
              wiPackage.productLine,
              wiPackage.ProductLine
            ),
            originalOEM: this.getFirstNormalizedValue(
              wiPackage.originalOEM,
              wiPackage.OriginalOEM,
              wiPackage.originalOem,
              wiPackage.originalOEMBrand
            ),
            frameType: this.getFirstNormalizedValue(
              wiPackage.frameType,
              wiPackage.FrameType
            ),
            model: this.getFirstNormalizedValue(
              wiPackage.model,
              wiPackage.Model,
              wiPackage.modelName
            ),
            partNumber: wiPackage.partNumber || '',
            documentNumber: wiPackage.documentNumber || '',
            sapReference: wiPackage.sapReference || '',
            revision: wiPackage.revision || '',
          };
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDeletedRows() {
      const response = await client.get('/work-instructions-package', {
        params: { deleted: true },
      });
      this.deletedRows = (response?.data || [])
        .filter((wiPackage) => this.isWorkInstructionType(wiPackage))
        .map((wiPackage) => ({
          _id: wiPackage._id,
          title: wiPackage.title || '-',
          owner: wiPackage.owner || '-',
          deletedAt: wiPackage.deletedAt || 0,
        }));
    },
    onAddWorkInstruction() {
      this.showCreateModal = true;
    },
    openEdit(row) {
      this.editingRow = {
        id: row.id,
        title: row.name,
        description: row.description === '-' ? '' : row.description,
        revision: row.revision,
        productLine: row.productLine,
        originalOEM: row.originalOEM,
        frameType: row.frameType,
        model: row.model,
        partNumber: row.partNumber,
        documentNumber: row.documentNumber,
        sapReference: row.sapReference,
      };
      this.showEditModal = true;
    },
    async onSaveEdit(payload) {
      this.showEditModal = false;
      if (!this.editingRow?.id) return;
      const body = {
        title: payload.title,
        description: payload.description,
        revision: payload.revision,
        productLine: payload.productLine,
        originalOEM: payload.originalOEM,
        frameType: payload.frameType,
        model: payload.model,
        partNumber: payload.partNumber,
        documentNumber: payload.documentNumber,
        sapReference: payload.sapReference,
        version: payload.revision || '1',
      };
      await client.put(
        `/work-instructions-package/${this.editingRow.id}`,
        body
      );
      await this.fetchRows();
    },
    confirmDelete(row) {
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: `Are you sure you want to delete <strong>${row.name}</strong>? This action cannot be undone.`,
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => this.deleteRow(row),
      });
    },
    async deleteRow(row) {
      await client.delete(`/work-instructions-package/${row.id}`);
      await this.fetchRows();
      await this.fetchDeletedRows();
    },
    async onSaveNewWorkInstruction(payload) {
      this.showCreateModal = false;

      const newPackage = {
        _id: uuid(),
        createdAt: new Date().toISOString(),
        isStandard: true,
        type: 'standard_work_instruction_package',
        jobId: 'standard',
        sortOrder: this.rows.length,
        workInstructions: [],
        status: 'Draft',
        owner: this.$store.state.currentUser?.fullname || '',
        version: payload.revision || '1',
        favoriteCount: 0,
        ...payload,
      };

      await client.post('/work-instructions-package', newPackage);
      await this.fetchRows();
    },
    showDeletedJobs() {
      this.fetchDeletedRows();
      this.isDeletedJobsVisible = true;
    },
    hideDeletedJobs() {
      this.isDeletedJobsVisible = false;
    },
    formatWIDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      if (isNaN(d)) return '-';
      return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
    },
    formatDate(epochDate) {
      if (!epochDate) return '-';
      const d = new Date(epochDate);
      if (isNaN(d)) return '-';
      return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
    },
    isUuidLike(value) {
      if (!value) return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value).trim()
      );
    },
    resolveRoutePackageId(wiPackage) {
      const candidates = [
        wiPackage?.uuid,
        wiPackage?.id,
        wiPackage?.packageId,
        wiPackage?._id,
      ]
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter(Boolean);

      return (
        candidates.find((candidate) => this.isUuidLike(candidate)) ||
        candidates[0] ||
        null
      );
    },
    async undelete(jobId) {
      await client.put(`/work-instructions-package/${jobId}/restore`);
      await this.fetchDeletedRows();
      await this.fetchRows();
    },
    openWorkInstruction(row) {
      const routePackageId = row?.routeId || row?.id;
      if (!routePackageId) {
        return;
      }

      this.$router.push({
        name: 'editor-work-instructions-package',
        params: { packageId: routePackageId },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.header {
  font-family: 'Siemens Sans';
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  padding-left: 20px;

  .list-title {
    flex: 1 1 auto;
    min-width: 0;
    height: 50px;
    line-height: 50px;
    padding: 0 10px;
  }

  .right-section {
    flex: 0 0 auto;
    display: flex;
    align-items: stretch;

    .search {
      height: 100%;
      padding: 5px 16px;
      background-color: #e0e0e0;

      :deep(.field) {
        margin-bottom: 0;
      }

      :deep(.search-input) {
        width: 230px;
      }

      .search-input input {
        border: 0 !important;
        background-color: #e0e0e0 !important;
        outline: none;
        box-shadow: none;
      }
    }
  }
}

.items {
  height: calc(100vh - 100px);
  overflow-y: auto;

  .items-container {
    padding: 45px 30px 10px 30px;
  }
}

.wi-add-button.button {
  border: none;
  border-radius: 0;
  height: 50px;
  min-width: 220px;
  padding: 0 18px;
  margin: 0;
  background: #4a78d0;
  color: #fff;
  font-family: 'Siemens Sans';
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.dij-jobs-recycle-bin.button {
  border: none;
  border-radius: 0;
  height: 50px;
  width: 50px;
  min-height: 50px;
  min-width: 50px;
  margin: 0;
  padding: 0;
}

.dij-jobs-recycle-bin.button :deep(.icon) {
  font-size: 2rem;
}

.wi-add-button.button:hover,
.wi-add-button.button:focus {
  color: #fff;
  background: #3e6ec7;
}

.favorite-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.favorite-cell .icon {
  color: #c6c6c6;
}

.favorite-cell .icon.active {
  color: #ffdd00;
}

.options-column {
  cursor: default;
  min-width: 60px;

  .options-more-btn {
    border: none;
    background: transparent;
    box-shadow: none;
    color: #ccc;

    &:hover {
      color: #333;
    }

    &:focus {
      box-shadow: none;
    }
  }
}

.wi-empty-state {
  padding: 2rem 1rem;
}

:deep(.table tbody tr) {
  cursor: pointer;
}
</style>
